import {
    CcRequirementHistory,
    CcRequirementReviewMethod,
    CcRequirementSmartCompliance,
} from "server/models"
import { APPROVED, ASC, YES, WORKFLOW_RERUN } from "const"
import { sort } from "client/store/utils"
import {
    RequirementList,
    RequirementParentChildPdfExport,
    RequirementRevisionActiveList,
    RequirementStateList,
    RequirementHistoryList,
    RequirementHistoryNestedList,

    CcRequirementTypeList,
    CcRequirementPriorityList,
    CcRequirementStatusList,
    CcRequirementReviewMethodList,
    CcRequirementSmartComplianceList,
    CcApprovalLevelList,
    CcResponseList,

    UserList,
    LangType,
    createFileName,
    getFile,
} from "client/store"
import { HistoryListPdfExport } from "client/components"

import * as Const from "client/components/pdfmake/template/const"

const listBottomMargin = [0, 0, 0, 10]
const assignedUserMargin = [20, 0, 0, 5]
const statusMargin = [20, 0, 0, 20]

const RequirementsPage = (
    requirementList: RequirementList,
    requirementIds: readonly number[],
    parentChildList: RequirementParentChildPdfExport,

    revisionList: RequirementRevisionActiveList,
    requirementStateList: RequirementStateList,
    requirementHistoryList: RequirementHistoryList,
    requirementHistoryNestedList: RequirementHistoryNestedList,

    typeList: CcRequirementTypeList,
    priorityList: CcRequirementPriorityList,
    statusList: CcRequirementStatusList,
    reviewMethodList: CcRequirementReviewMethodList,
    smartComplianceList: CcRequirementSmartComplianceList,

    levelList: CcApprovalLevelList,
    levelIds: number[],
    responseList: CcResponseList,
    userList: UserList,
    lang: LangType,
) => {
    const content: any[] = [
        {
            pageBreak: "before",
            style: Const.HEADER,
            text: `3.   ${lang.requirements}`,
            ...Const.TOC_HEADER_BASE,
        },
    ]

    requirementIds.forEach((requirementId, index) => {
        const requirement = requirementList[requirementId]
        const revision = revisionList[requirementId]

        const type = typeList[requirement.typeId] || null
        const priority = priorityList[requirement.priorityId] || null
        const status = statusList[requirement.statusId] || null

        const reviewMethods: CcRequirementReviewMethod[] = []
        Object.values(reviewMethodList).forEach((reviewMethod: CcRequirementReviewMethod) => {
            if (requirement.reviewMethodsParsed[reviewMethod.id]) reviewMethods.push(reviewMethod)
        })
        reviewMethods.sort((a, b) => sort(a, b, "name", ASC))
        const reviewMethodsList: string[] = []
        reviewMethods.forEach((reviewMethod) => {
            reviewMethodsList.push(reviewMethod.name)
        })

        const smartCompliances: CcRequirementSmartCompliance[] = []
        Object.values(smartComplianceList).forEach((smartCompliance: CcRequirementSmartCompliance) => {
            if (requirement.smartComplianceParsed[smartCompliance.id]) smartCompliances.push(smartCompliance)
        })
        smartCompliances.sort((a, b) => sort(a, b, "name", ASC))
        const smartCompliancesList: string[] = []
        smartCompliances.forEach((smartCompliance) => {
            smartCompliancesList.push(smartCompliance.name)
        })

        const phaseList: string[] = []
        requirement.phaseLinks.forEach((phaseLink) => {
            phaseList.push(`${phaseLink.phase.lifecycle.name} > ${phaseLink.phase.name}`)
        })

        const parentList: string[] = []
        const childList: string[] = []
        const { parents, children } = parentChildList[requirementId]
        parents.forEach((parentId) => {
            const parent = requirementList[parentId]
            parentList.push(`${parent.uniqueId}  -  ${parent.name}`)
        })
        children.forEach((childId) => {
            const child = requirementList[childId]
            childList.push(`${child.uniqueId}  -  ${child.name}`)
        })

        const stack: any[] = [
            `${lang.uniqueId}: ${requirement.uniqueId}`,
            `${lang.name}: ${requirement.name}`,
            `${lang.description}: ${requirement.description}`,
        ]

        if (requirement.file && requirement.file.versions.length > 0) {
            stack.push({
                text: [
                    `${lang.file}: `,
                    {
                        text: createFileName(requirement.file),
                        color: "blue",
                        decoration: "underline",
                        link: getFile(requirement.file, requirement.file.versions[0]).download,
                    },
                ],
            })
        } else {
            stack.push(`${lang.file}: ${lang.empty}`)
        }

        stack.push(
            `${lang.type}: ${type ? type.name : lang.notAssigned}`,
            `${lang.priority}: ${priority ? priority.name : lang.notAssigned}`,
            {
                text: `${lang.status}: ${status ? status.name : lang.notAssigned}`,
                margin: listBottomMargin,
            },
        )

        if (reviewMethodsList.length > 0) {
            stack.push(
                `${lang.reviewMethods}:`,
                {
                    style: Const.LIST,
                    ul: reviewMethodsList,
                },
            )
        } else {
            stack.push({
                text: `${lang.reviewMethods}: ${lang.empty}`,
                margin: listBottomMargin,
            })
        }

        if (smartCompliancesList.length > 0) {
            stack.push(
                `${lang.smartCompliances}:`,
                {
                    style: Const.LIST,
                    ul: smartCompliancesList,
                },
            )
        } else {
            stack.push({
                text: `${lang.smartCompliances}: ${lang.empty}`,
                margin: listBottomMargin,
            })
        }

        if (parentList.length > 0) {
            stack.push(
                `${lang.parents}:`,
                {
                    style: Const.LIST,
                    ul: parentList,
                },
            )
        } else {
            stack.push({
                text: `${lang.parents}: ${lang.empty}`,
                margin: listBottomMargin,
            })
        }

        if (childList.length > 0) {
            stack.push(
                `${lang.children}:`,
                {
                    style: Const.LIST,
                    ul: childList,
                },
            )
        } else {
            stack.push({
                text: `${lang.children}: ${lang.empty}`,
                margin: listBottomMargin,
            })
        }

        if (phaseList.length > 0) {
            stack.push(
                `${lang.lifecyclePhases}:`,
                {
                    style: Const.LIST,
                    ul: phaseList,
                },
            )
        } else {
            stack.push({
                text: `${lang.lifecyclePhases}: ${lang.empty}`,
                margin: listBottomMargin,
            })
        }

        content.push(
            {
                style: Const.SUB_HEADER,
                text: [
                    `3.${index + 1}  ${requirement.uniqueId}  —  ${requirement.name}`,
                    revision && `  —  ${lang.reviewRevision}: ${revision.name}`,
                ],
                ...Const.TOC_ITEM_BASE,
                pageBreak: index > 0 && "before",
            },
            {
                stack,
                lineHeight: 1.25,
                alignment: "justify",
                margin: [0, 0, 0, 10],
            },
        )

        // state - same as in requirement review columns
        const requirementHistory = requirementHistoryList[requirementId] || []
        let lastHistory: CcRequirementHistory = null

        // get the last history row made by user, not by 'system'
        for (let i = requirementHistory.length - 1; i >= 0; i--) {
            const history = requirementHistory[i]
            if (history.workflow === WORKFLOW_RERUN) {
                break
            }
            if (!history.systemMessage) {
                lastHistory = history
                break
            }
        }

        const requirementState = requirementStateList[requirementId] || null
        const level = requirementState ? levelList[requirementState.levelId] : null
        const verifier = requirementState ? userList[requirementState.verifierId] : null

        if (lastHistory && lastHistory.nextStatus === APPROVED) {
            const response = responseList[lastHistory.responseId]
            content.push({
                text: `${lang.status}: ${response && response.approved === YES ? lang.approved : lang.notApproved}`,
                margin: statusMargin,
            })
        } else if (level) {
            const firstLevel = level.id === levelIds[0]
            const verifierName = verifier ? `${verifier.firstName} ${verifier.lastName}` : lang.notAssigned
            content.push(
                {
                    text: `${level.name}: ${verifierName}`,
                    margin: assignedUserMargin,
                },
                {
                    text: `${lang.status}: ${firstLevel ? lang.open : lang.verification}`,
                    margin: statusMargin,
                },
            )
        } else if (!level && lastHistory) { // approval level was removed or not set
            content.push(
                {
                    text: `${lang.status}: ${lang.verification}`,
                    margin: statusMargin,
                },
            )
        } else if (!level && !lastHistory) { // no any data
            content.push(
                {
                    text: `${lang.status}: ${lang.open}`,
                    margin: statusMargin,
                },
            )
        }

        // history
        const historyNested = requirementHistoryNestedList[requirementId]
        if (historyNested.length > 0) {
            // table wrapper around a requirement history is required to keep the page structure
            content.push({
                layout: Const.ITEM_HISTORY_TABLE_LAYOUT,
                table: {
                    widths: ["*"],
                    body: [
                        [{ stack: HistoryListPdfExport(historyNested, userList, levelList, responseList, lang) }],
                    ],
                },
            })
        }
    })

    return content
}
export default RequirementsPage
